import InfiniteScroll from 'react-infinite-scroll-component';
import dayjs from 'dayjs';
import { useContext, useMemo } from 'react';
import { useMeetingRecallDocumentsInfiniteQuery } from '@/modules/common/queries/meetings.ts';
import DocumentPreviewModal from '@/modules/admin-dashboard/components/modals/DocumentPreviewModal.tsx';
import { Button, ModalProviderContext, Spinner } from 'ui';
import { UserDocumentResponse } from 'compliance-api';

interface MeetingRecallDocumentsProps {
  meetingId: number;
  open: boolean;
}

export const MeetingRecallDocuments = ({ meetingId, open }: MeetingRecallDocumentsProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const {
    data: recallDocumentsResponse,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useMeetingRecallDocumentsInfiniteQuery({ meetingId }, { disabled: !open || !meetingId });

  const recallDocumentItems = useMemo(
    () => (recallDocumentsResponse?.pages || []).flatMap(({ data }) => data),
    [recallDocumentsResponse?.pages],
  );

  const groupedRecallDocuments = useMemo(() => {
    const groups = new Map<string, typeof recallDocumentItems>();

    for (const entry of recallDocumentItems) {
      const groupKey = entry.date ? dayjs(entry.date).format('DD MMM YYYY') : 'Unknown date';
      const current = groups.get(groupKey) || [];
      current.push(entry);
      groups.set(groupKey, current);
    }

    return Array.from(groups.entries());
  }, [recallDocumentItems]);

  const handleOpenDocument = (doc?: UserDocumentResponse) => {
    if (!doc) return;
    openModal(DocumentPreviewModal, { doc, isLargePreviewAlways: true });
  };

  return (
    <div className="flex flex-col gap-6 py-8 px-6">
      <div className="flex items-center gap-2">
        <span className="text-sm leading-[22px] text-gray-900 font-medium">Meeting Documents</span>
        <span className="text-sm leading-[22px] text-gray-500 font-medium">
          {recallDocumentItems.length}
        </span>
      </div>

      {isLoading && (
        <div className="flex h-full w-full items-center justify-center py-4">
          <Spinner size="md" variant="primary" />
        </div>
      )}

      {!isLoading && !groupedRecallDocuments.length && (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-500">
          No meeting documents yet.
        </div>
      )}

      {!isLoading && !!groupedRecallDocuments.length && (
        <InfiniteScroll
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          dataLength={recallDocumentItems.length}
          className="flex flex-col gap-5"
          scrollableTarget="modal_scroll"
        >
          {groupedRecallDocuments.map(([groupDate, entries]) => (
            <div key={groupDate} className="flex flex-col gap-3">
              <div className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
                {groupDate}
              </div>

              {entries.map((entry, index) => (
                <div
                  key={`${groupDate}-${entry.id}-${entry.summary_document?.id || 'summary'}-${
                    entry.transcript_document?.id || 'transcript'
                  }-${index}`}
                  className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4"
                >
                  <div className="mb-3 text-xs text-gray-500">
                    {entry.date
                      ? dayjs(entry.date).format('DD MMM YYYY, HH:mm')
                      : 'Unknown processed time'}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      { label: 'Summary document', doc: entry.summary_document },
                      { label: 'Transcript document', doc: entry.transcript_document },
                    ].map(({ label, doc }) => (
                      <div
                        key={label}
                        className="rounded-xl border border-white bg-white p-4 shadow-sm"
                      >
                        <div className="mb-2 text-xs text-gray-500">{label}</div>
                        {doc ? (
                          <>
                            <div className="mb-3 text-sm font-medium text-gray-900 break-all">
                              {doc.name}.{doc.format}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="secondary"
                                className="w-fit"
                                onClick={() => handleOpenDocument(doc)}
                              >
                                View
                              </Button>
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noreferrer"
                                download
                                className="text-sm font-medium text-blue-700 hover:underline"
                              >
                                Download
                              </a>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-500">Document unavailable.</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </InfiniteScroll>
      )}
    </div>
  );
};
